import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ChevronDown, FileSearch, ShieldAlert, Sparkles, Target, Unlock, Scale, Cog } from "lucide-react";
import { reports, EVIDENCE_NOTE, VERDICT_FOOTNOTE } from "../data/scams";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Mascot } from "../components/Mascot";
import { PlatformBadge } from "../components/ScamCard";
import { riskStyles, confidenceStyles, getSignalStyle } from "../lib/risk";

const pillarIcons = { dream: Sparkles, goal: Target, catch: Unlock };

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
});

const SignalList = ({ title, icon: Icon, items, testid }) => (
  <motion.section {...fadeUp()} className="mt-10" data-testid={testid}>
    <div className="flex items-center gap-2.5">
      <Icon className="w-5 h-5 text-[#15803D]" strokeWidth={2.2} />
      <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#141915]">{title}</h2>
    </div>
    <div className="mt-4 rounded-3xl border border-[#E5DFD7] bg-white divide-y divide-[#F4EFEA]">
      {items.map((item, i) => (
        <div key={i} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 px-5 py-3.5">
          <span className="text-sm font-semibold text-[#141915]">{item.name}</span>
          <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${getSignalStyle(item.status)}`}>
            {item.status}
          </span>
        </div>
      ))}
    </div>
  </motion.section>
);

export default function Report() {
  const { slug } = useParams();
  const r = reports[slug];
  const [receiptsOpen, setReceiptsOpen] = useState(false);

  if (!r) return <Navigate to="/" replace />;

  const risk = riskStyles[r.riskKey];
  const isReal = r.caseType === "real";

  return (
    <div className="min-h-screen">
      <Header />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-20">
        <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
          <Link
            to="/"
            data-testid="back-to-top10-link"
            className="inline-flex items-center gap-2 rounded-full border border-[#E5DFD7] bg-white px-3.5 py-1.5 text-xs font-semibold text-[#525E54] transition-all duration-300 hover:gap-3 hover:text-[#15803D] hover:border-[#BBE7C6] active:scale-95"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
            Back to Top 10
          </Link>
        </motion.div>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6"
          data-testid="report-hero"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-[#15803D]">
              #{r.rank} Trending — 30-Second Scoop
            </span>
            {isReal ? (
              <span
                className="rounded-full border border-[#FDA4AF] bg-[#FFE4E6] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#9F1239]"
                data-testid="case-badge"
              >
                Real-World Case Study • Lizard-Checked {r.checkedDates}
              </span>
            ) : (
              <span
                className="rounded-full border border-[#E5DFD7] bg-[#F4EFEA] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[#7B887D]"
                data-testid="demo-badge"
              >
                Demo Report • Fictional Sample Data
              </span>
            )}
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#141915] leading-[1.05]">
            {r.name}
          </h1>

          <div className="mt-5 rounded-3xl border border-[#E5DFD7] bg-white p-5 sm:p-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
              <div className="flex items-end gap-2">
                <span className="font-mono text-5xl sm:text-6xl font-bold tracking-tighter leading-none" style={{ color: risk.text }} data-testid="report-risk-score">
                  {r.score.toFixed(1)}
                </span>
                <span className="font-mono text-sm text-[#7B887D] pb-1">/ 10</span>
              </div>
              <div className="flex flex-col gap-1.5">
                <span
                  className="w-fit rounded-full border px-2.5 py-1 font-mono text-[11px] font-bold uppercase tracking-wider"
                  style={{ backgroundColor: risk.bg, color: risk.text, borderColor: risk.border }}
                  data-testid="report-risk-label"
                >
                  {r.riskLabel}
                </span>
                <span
                  className={`w-fit rounded-full border px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-wider ${confidenceStyles[r.confidence]}`}
                  data-testid="report-confidence"
                >
                  Evidence Confidence: {r.confidence}
                </span>
              </div>
              <div className="h-2 flex-1 min-w-[120px] overflow-hidden rounded-full bg-[#F4EFEA]">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${r.score * 10}%` }}
                  transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="h-full rounded-full"
                  style={{ backgroundColor: risk.bar }}
                />
              </div>
            </div>
          </div>
        </motion.section>

        <section className="mt-8 grid gap-3.5" data-testid="report-pillars">
          {r.pillars.map((p, i) => {
            const Icon = pillarIcons[p.id];
            return (
              <motion.div
                key={p.id}
                {...fadeUp(i * 0.08)}
                className="rounded-3xl border border-[#E5DFD7] bg-white p-5 sm:p-6"
                data-testid={`report-the-${p.id}-card`}
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#EBF7EE] border border-[#BBE7C6]">
                    <Icon className="w-4 h-4 text-[#15803D]" strokeWidth={2.2} />
                  </span>
                  <h2 className="font-mono text-xs font-bold uppercase tracking-[0.18em] text-[#141915]">{p.title}</h2>
                </div>
                <p className="mt-3 font-display text-lg sm:text-xl font-medium text-[#2D372E] leading-snug">{p.body}</p>
              </motion.div>
            );
          })}
        </section>

        {isReal && (
          <section className="mt-12" data-testid="receipts-section">
            <motion.div {...fadeUp()}>
              <div className="flex items-center gap-2.5">
                <FileSearch className="w-5 h-5 text-[#15803D]" strokeWidth={2.2} />
                <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#141915]">
                  Evidence Reviewed
                </h2>
              </div>
              <p className="mt-1.5 text-sm text-[#525E54]">The receipts behind this case.</p>
            </motion.div>
            <div className="mt-5 flex flex-col gap-3">
              <div className="flex items-center gap-2.5 rounded-2xl border border-dashed border-[#BBE7C6] bg-[#EBF7EE] px-4 py-3">
                <Mascot className="w-7 h-7 shrink-0" />
                <p className="font-display italic text-sm text-[#14532D]">"Yeah... we checked."</p>
              </div>
              {r.receipts.map((rec, i) => (
                <motion.div
                  key={rec.id}
                  {...fadeUp(Math.min(i * 0.06, 0.2))}
                  className="rounded-3xl border border-[#E5DFD7] bg-white p-5"
                  data-testid={`receipt-card-${i + 1}`}
                >
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <span className="font-mono text-[10px] font-bold text-[#141915]/30">
                      #{String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-display text-base sm:text-lg font-semibold text-[#141915]">{rec.title}</h3>
                    <span className="rounded-full border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider bg-[#FFE4E6] border-[#FDA4AF] text-[#9F1239]">
                      {rec.tag}
                    </span>
                  </div>
                  {rec.image && (
                    <img
                      src={rec.image}
                      alt={rec.caption || rec.title}
                      loading="lazy"
                      className="mt-3 w-full max-h-[520px] object-contain rounded-2xl border border-[#E5DFD7] bg-[#F4EFEA]"
                      data-testid={`receipt-image-${i + 1}`}
                    />
                  )}
                  {rec.body && <p className="mt-2 text-sm text-[#525E54] leading-relaxed">{rec.body}</p>}
                  {rec.caption && <p className="mt-2.5 text-xs italic text-[#7B887D] leading-relaxed">{rec.caption}</p>}
                </motion.div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-12" data-testid="what-lizard-found">
          <motion.div {...fadeUp()}>
            <div className="flex items-center gap-2.5">
              <ShieldAlert className="w-5 h-5 text-[#C2410C]" strokeWidth={2.2} />
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#141915]">
                What the Lizard Found
              </h2>
            </div>
            <p className="mt-1.5 text-sm text-[#525E54]">{r.findingsSub}</p>
          </motion.div>

          <div className="mt-5 flex flex-col gap-3">
            {r.findings.map((f, i) => (
              <motion.div
                key={f.id}
                {...fadeUp(Math.min(i * 0.05, 0.25))}
                className="rounded-3xl border border-[#E5DFD7] bg-white p-5"
                data-testid={`finding-${f.id}`}
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <h3 className="font-mono text-xs font-bold uppercase tracking-[0.14em] text-[#141915]">{f.title}</h3>
                  {f.status && (
                    <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider ${getSignalStyle(f.status)}`}>
                      {f.status}
                    </span>
                  )}
                </div>
                <p className="mt-2 text-sm text-[#525E54] leading-relaxed">{f.body}</p>
                {f.stats && (
                  <div className="mt-3.5 grid grid-cols-2 gap-2.5">
                    {f.stats.map((s) => (
                      <div key={s.label} className="rounded-2xl bg-[#F4EFEA] px-3.5 py-3">
                        <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-[#7B887D]">{s.label}</p>
                        <p className="mt-1 font-mono text-lg sm:text-xl font-bold tracking-tight text-[#141915]">{s.value}</p>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </section>

        {r.trustTactics && <SignalList title="Trust Tactics" icon={Scale} items={r.trustTactics} testid="trust-tactics-section" />}
        {r.riskMechanics && <SignalList title="Risk Mechanics" icon={Cog} items={r.riskMechanics} testid="risk-mechanics-section" />}

        <motion.section {...fadeUp()} className="mt-10 grid gap-3 sm:grid-cols-2" data-testid="report-meta">
          <div className="rounded-3xl border border-[#E5DFD7] bg-white p-5">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[#7B887D]">Evidence Source</p>
            <p className="mt-2 text-sm font-semibold text-[#141915] leading-snug" data-testid="report-evidence-source">{r.evidenceSource}</p>
          </div>
          <div className="rounded-3xl border border-[#E5DFD7] bg-white p-5">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[#7B887D]">Last reviewed</p>
            <p className="mt-2 font-display text-xl font-semibold text-[#141915]" data-testid="report-last-reviewed">{r.lastReviewed}</p>
          </div>
          {r.platforms && (
            <div className="rounded-3xl border border-[#E5DFD7] bg-white p-5">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[#7B887D]">Seen advertised on</p>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {r.platforms.map((p) => (
                  <PlatformBadge key={p} name={p} />
                ))}
              </div>
            </div>
          )}
          {r.platformInfo &&
            r.platformInfo.map((info) => (
              <div key={info.label} className="rounded-3xl border border-[#E5DFD7] bg-white p-5">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[#7B887D]">{info.label}</p>
                <p className="mt-2 text-sm font-semibold text-[#141915] leading-snug">{info.value}</p>
              </div>
            ))}
        </motion.section>

        <motion.p {...fadeUp()} className="mt-4 text-[11px] text-[#7B887D] leading-relaxed" data-testid="evidence-note">
          {EVIDENCE_NOTE}
        </motion.p>

        <motion.section
          {...fadeUp()}
          className="mt-10 rounded-3xl bg-[#141915] px-6 py-7 sm:px-8"
          data-testid="why-rated-section"
        >
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[#22C55E]">
            {r.verdictTitle}
          </p>
          <p className="mt-3 text-sm sm:text-base text-white/80 leading-relaxed">{r.verdictBody}</p>
          <p className="mt-4 border-t border-white/10 pt-4 text-xs text-white/50 leading-relaxed italic">
            {r.verdictFootnote || VERDICT_FOOTNOTE}
          </p>
        </motion.section>

        {!isReal && (
        <section className="mt-10" data-testid="receipts-section">
          <motion.button
            {...fadeUp()}
            onClick={() => setReceiptsOpen((o) => !o)}
            data-testid="receipts-toggle"
            aria-expanded={receiptsOpen}
            className="w-full flex items-center justify-between gap-4 rounded-3xl border border-[#2D372E] bg-white px-6 py-5 text-left transition-colors duration-300 hover:bg-[#F4EFEA] active:scale-[0.99]"
          >
            <span className="flex items-center gap-3">
              <FileSearch className="w-5 h-5 text-[#15803D]" strokeWidth={2.2} />
              <span className="font-display text-lg sm:text-xl font-bold text-[#141915]">Show the Receipts</span>
            </span>
            <motion.span animate={{ rotate: receiptsOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
              <ChevronDown className="w-5 h-5 text-[#525E54]" strokeWidth={2.2} />
            </motion.span>
          </motion.button>

          <AnimatePresence initial={false}>
            {receiptsOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="pt-4 flex flex-col gap-3">
                  <div className="flex items-center gap-2.5 rounded-2xl border border-dashed border-[#BBE7C6] bg-[#EBF7EE] px-4 py-3">
                    <Mascot className="w-7 h-7 shrink-0" />
                    <p className="font-display italic text-sm text-[#14532D]">"Yeah... we checked."</p>
                  </div>
                  {r.receipts.map((rec, i) => (
                    <motion.div
                      key={rec.id}
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.08 + i * 0.06 }}
                      className="rounded-3xl border border-[#E5DFD7] bg-white p-5"
                      data-testid={`receipt-card-${i + 1}`}
                    >
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        <span className="font-mono text-[10px] font-bold text-[#141915]/30">
                          #{String(i + 1).padStart(2, "0")}
                        </span>
                        <h3 className="font-display text-base sm:text-lg font-semibold text-[#141915]">{rec.title}</h3>
                        <span
                          className={`rounded-full border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${
                            isReal
                              ? "bg-[#FFE4E6] border-[#FDA4AF] text-[#9F1239]"
                              : "bg-[#FEF08A] border-[#FCD34D] text-[#713F12]"
                          }`}
                        >
                          {rec.tag}
                        </span>
                      </div>
                      {rec.image && (
                        <img
                          src={rec.image}
                          alt={rec.caption || rec.title}
                          loading="lazy"
                          className="mt-3 w-full max-h-[520px] object-contain rounded-2xl border border-[#E5DFD7] bg-[#F4EFEA]"
                          data-testid={`receipt-image-${i + 1}`}
                        />
                      )}
                      {rec.body && <p className="mt-2 text-sm text-[#525E54] leading-relaxed">{rec.body}</p>}
                      {rec.caption && <p className="mt-2.5 text-xs italic text-[#7B887D] leading-relaxed">{rec.caption}</p>}
                    </motion.div>
                  ))}
                  {!isReal && (
                    <p className="px-1 text-[11px] text-[#7B887D] leading-relaxed">
                      All evidence shown is fictional demonstration content created for this concept prototype.
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
        )}

        <motion.div {...fadeUp()} className="mt-12 text-center">
          <Link
            to="/"
            data-testid="report-bottom-back-link"
            className="inline-flex items-center gap-2 rounded-full bg-[#141915] px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#15803D] hover:gap-3 active:scale-95"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
            Back to the Top 10
          </Link>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
